import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TupAuthService, UserContainer } from '@telmar-global/tup-auth';
import {
  TupDocument,
  TupDocumentEvent,
  TupDocumentEventEmitterDirective,
  TupDocumentEventHandlerDirective,
  TupDocumentEventType,
  TupDocumentService,
  TupDocumentStorageComponent,
  TupDocumentTypeId,
  TupDocumentTypes,
  TupUserContainerService,
  isNotNullOrUndefined,
} from '@telmar-global/tup-document-storage';
import {
  TupSaveDocumentDialogData,
  TupSaveDocumentDialogOptions,
  TupSaveDocumentDialogResult,
  TupUserMessageService,
  getSaveDocumentDialogData,
} from '@telmar-global/tup-user-message';
import { isNil, merge, omitBy, pick } from 'lodash';
import { Subject } from 'rxjs';
import { concatMap, takeUntil, tap } from 'rxjs/operators';
import { AppPackage } from 'src/app/app.package';
import { SurveyTimeDocument, ViewType } from 'src/app/models';
import {
  CreateNewDocumentService,
  DocumentService,
  ImportService,
} from 'src/app/services';

@Component({
  selector: 'app-dashboard-quick-reports',
  templateUrl: './dashboard-quick-reports.component.html',
  styleUrls: ['./dashboard-quick-reports.component.scss'],
})
export class DashboardQuickReportsComponent implements OnInit, OnDestroy {
  private currentContainer: UserContainer;
  private unsubscribe: Subject<void> = new Subject<void>();

  @ViewChild(TupDocumentEventEmitterDirective, { static: true })
  public documentEventEmitter: TupDocumentEventEmitterDirective;
  @ViewChild(TupDocumentEventHandlerDirective, { static: false })
  public documentEventHandler: TupDocumentEventHandlerDirective;
  @ViewChild(TupDocumentStorageComponent, { static: true })
  public documentStorage: TupDocumentStorageComponent;
  @ViewChild('deleteDocDialogContent') deleteDocDialogContent: TemplateRef<any>;
  @ViewChild('deleteSharedDocDialogContent')
  deleteSharedDocDialogContent: TemplateRef<any>;
  @ViewChild('deleteOtherDocDialogContent')
  deleteOtherDocDialogContent: TemplateRef<any>;

  public isLoading = false;
  public isPageLoading = false;
  public documentOwnerName = '';
  public documentName = '';
  public userEmail: string;
  public docOptionsType = TupDocumentTypeId.SURVEYTIME_CAMPAIGN_TEMPLATE;
  public actions = [
    'click:Start from quick report:add_card::true',
    'click:edit:edit::true',
    'click:duplicate:file_copy::true',
    'click:delete:highlight_off',
    'click:export:file_download::true',
    'update_tags',
    'update_container',
  ];

  constructor(
    private router: Router,
    private userMessageService: TupUserMessageService,
    private tupDocumentService: TupDocumentService,
    private documentService: DocumentService,
    private importService: ImportService,
    private userContainerService: TupUserContainerService,
    private createNewDocumentService: CreateNewDocumentService,
    private authService: TupAuthService
  ) {}

  ngOnInit(): void {
    this.getUserEmail();
    this.listenToContainerChange();

    this.userContainerService.getContainer();
    // Handle default TupDocumentEvents
    this.documentEventEmitter.click.subscribe((event: TupDocumentEvent) => {
      if (event.data) {
        const document = event.document;
        switch (event.data.action) {
          case 'edit':
            this.openExisting(event.document);
            break;
          case 'Start from quick report':
            this.startFrom(event.document);
            break;
          case 'duplicate':
            this.duplicateDoc(document);
            break;
          case 'delete':
            const { metadata } = document;
            this.documentOwnerName = metadata.by.attributes.name;
            this.documentName = metadata.name;
            const isOwner = this.isDocumentOwner(document);
            const isShared = this.isDocumentInSharedContainer();
            let dialogContent = this.deleteDocDialogContent;
            let confirmText = 'Delete';
            let cancelText = 'Keep';
            if (!isOwner) {
              dialogContent = this.deleteOtherDocDialogContent;
              confirmText = 'OK';
              cancelText = null;
            } else if (isShared) {
              dialogContent = this.deleteSharedDocDialogContent;
              confirmText = 'Yes, delete';
              cancelText = 'Cancel';
            }
            this.userMessageService
              .openCustomMessageDialog(dialogContent, 'Delete document', {
                confirmText,
                cancelText,
                centered: true,
                width: '400px',
              })
              .afterClosed()
              .subscribe((result) => {
                this.resetDocumentOwnerName();
                if (!result) {
                  return;
                }
                this.isLoading = true;
                this.tupDocumentService
                  .delete(metadata.container.name, metadata.id)
                  .subscribe(() => {
                    this.documentEventHandler.reload.emit(true);
                    this.isLoading = false;
                  });
              });
            break;
          case 'export':
            const doc = event.document;
            this.exportJSON(doc);
            break;
        }
      } else {
        this.openExisting(event.document);
      }
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public import(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    this.isLoading = true;
    const file = target.files[0];
    this.importService
      .importJSON(
        file,
        this.userEmail,
        TupDocumentTypeId.SURVEYTIME_CAMPAIGN_TEMPLATE
      )
      .subscribe(
        (res) => {
          this.isLoading = false;
          this.documentEventHandler.reload.emit(true);
        },
        (error) => {
          this.isLoading = false;
          const duration = 5000;
          this.userMessageService.showSnackBar(
            `Error importing document - ${file.name}: document isn't valid`,
            'OK',
            duration
          );
        }
      );
  }

  public createQuickReport(): void {
    const isTemplateDocType = true;
    this.createNewDocumentService.create(isTemplateDocType);
  }

  public openExisting(doc: TupDocument<unknown>): void {
    const currentDoc = Object.assign(doc) as TupDocument<SurveyTimeDocument>;
    this.router.navigate([`doc/${currentDoc.metadata.id}/chart`]);
    return;
  }

  public startFrom(doc: TupDocument<unknown>): void {
    this.isPageLoading = true;

    const userContainer: UserContainer = this.authService.user.containers.find(
      (container) => container.name === this.userEmail
    );

    this.tupDocumentService
      .get(doc?.metadata.container.name, doc?.metadata.id)
      .subscribe((docToClone: TupDocument<SurveyTimeDocument>) => {
        const newDoc: TupDocument<SurveyTimeDocument> = {
          ...docToClone,
          metadata: {
            ...docToClone.metadata,
            type: TupDocumentTypes[TupDocumentTypeId.SURVEYTIME_CAMPAIGN],
          },
        };

        this.createDocument(userContainer, newDoc);
      });
  }

  private async createDocument(
    container: UserContainer,
    doc: TupDocument<SurveyTimeDocument>
  ): Promise<void> {
    const docId = await this.documentService
      .createDocument(container?.name, null, doc)
      .toPromise();

    const surveys = doc.content.surveys;
    this.router.navigate([`doc/${docId}/data`], {
      queryParams: {
        tab: ViewType.crossTab,
      },
      state: { hasDocument: true, surveys },
    });

    this.userContainerService.setContainer(container);
  }

  private exportJSON(doc: TupDocument<unknown>): void {
    const documentName = doc.metadata.name;
    const download = (data: string, filename: string, type: string) => {
      var file = new Blob([data], { type: type });
      var a = window.document.createElement('a');
      var url = URL.createObjectURL(file);
      a.href = url;
      a.download = filename;
      window.document.body.appendChild(a);
      a.click();
      setTimeout(function () {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 0);
    };
    this.tupDocumentService
      .get(doc.metadata.container.name, doc.metadata.id)
      .subscribe((fullDoc: TupDocument<SurveyTimeDocument>) => {
        const docWithVersion = Object.assign(fullDoc, {
          version: AppPackage.version,
        });

        const fileSpace = 2;

        const data = JSON.stringify(docWithVersion, null, fileSpace);
        download(data, `${documentName}_quick_report.json`, 'json');
      });
  }

  private duplicateDoc(doc: TupDocument<unknown>): void {
    const dialogOptions: TupSaveDocumentDialogOptions = {
      nameFormControlValue: doc.metadata.name,
      descriptionFormControlValue: doc.metadata.description,
      enableSaveOnInit: true,
    };

    const dialogData: TupSaveDocumentDialogData = getSaveDocumentDialogData(
      'Duplicate',
      'Document',
      dialogOptions
    );

    const dialogConfig: MatDialogConfig = { data: dialogData };

    let _dialogResult: TupSaveDocumentDialogResult;

    this.userMessageService
      .openSaveDocumentDialog(dialogConfig)
      .afterClosed()
      .pipe(
        isNotNullOrUndefined(),
        tap(
          (dialogResult: TupSaveDocumentDialogResult) =>
            (_dialogResult = dialogResult)
        ),
        // AE-168 Fetch the entire document before duplicating it
        concatMap(() => {
          return this.tupDocumentService.get(
            doc.metadata.container.name,
            doc.metadata.id
          );
        })
      )
      .subscribe((_document: TupDocument<SurveyTimeDocument>) => {
        const document: TupDocument<SurveyTimeDocument> = merge(
          {},
          pick(_document, ['metadata.type', 'metadata.status', 'content']),
          { metadata: omitBy(_dialogResult, isNil) }
        );

        this.documentEventEmitter.emit(document, TupDocumentEventType.CREATE);
      });
  }

  private isDocumentOwner(doc: TupDocument<unknown>): boolean {
    return doc.metadata.by.attributes.email === this.userEmail;
  }

  private isDocumentInSharedContainer(): boolean {
    return this.currentContainer.name !== this.userEmail;
  }

  private resetDocumentOwnerName(): void {
    this.documentOwnerName = null;
    this.documentName = null;
  }

  private getUserEmail() {
    this.userEmail = this.authService.user.attributes.email;
  }

  private listenToContainerChange() {
    this.userContainerService.container
      .pipe(isNotNullOrUndefined(), takeUntil(this.unsubscribe))
      .subscribe((container: UserContainer) => {
        this.currentContainer = container;
      });
  }
}
